import { useState, useMemo } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Popover,
  Snackbar,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  AssigneeCandidate,
  DataAcquisition,
  GetAssigneeCandidatesResponse,
  GetDataAcquisitionsResponse,
  UpdateAssigneeBody,
  UserSummary,
} from "@aeriosiq/types";
import { dataAcquisitionKeys } from "./queries.js";
import { PencilIcon } from "@aeriosiq/ui/icons";

const UNASSIGNED_OPTION: AssigneeCandidate = {
  id: "__unassigned__",
  displayName: "Unassign",
  initials: "—",
  tenantId: "00000000-0000-0000-0000-000000000000",
  tenantSlug: "",
  tenantDisplayName: null,
  sameTenant: true,
};

interface UpdateContext {
  previousLists: {
    key: readonly unknown[];
    data: GetDataAcquisitionsResponse;
  }[];
}

/**
 * Story 016 — Inline assignee edit.
 *
 * Editable chip in the Inspections-list Assignee column. Click → popover
 * with searchable Autocomplete sourced from
 * `GET /data-acquisitions/{id}/assignee-candidates` (resolved per-inspection by
 * the BE via ProjectRoleAccessMap, so cross-tenant assignees are allowed
 * when the project grants it). Optimistic update on the row's
 * `assignedTo`; rolls back on error and surfaces a Snackbar.
 *
 * Cross-tenant transparency: when the picked assignee's `sameTenant` is
 * false we render their tenant short-code as a small subscript, mirroring
 * the user's Q6 answer in `.vibe/23`.
 */
export interface AssigneeChipProps {
  inspectionId: string;
  /** Current assignee (resolved from `usersById` by the parent — null
   * when the row's `assignedTo` is null OR the lookup hasn't loaded). */
  current: UserSummary | undefined;
  /** Fallback string shown when `current` is undefined (raw UUID from
   * the row, or "Unassigned" when the row's `assignedTo` is null). */
  fallback: string | null;
}

export function AssigneeChip({
  inspectionId,
  current,
  fallback,
}: AssigneeChipProps) {
  const api = useApi();
  const qc = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // SB2-W-03 — source from the narrowed claim-eligible roster
  // (`/data-acquisitions/{id}/assignable-users`) instead of the wider
  // `/assignee-candidates`. Closes the [00:02:09] 500 by routing through
  // the typed endpoint that filters to {platform_admin, tenant_admin,
  // expert_analyst, pre_screener} server-side. The old endpoint stays
  // wired (the AssigneeFilter uses it for the toolbar filter) but the
  // per-row dropdown speaks SB2.
  //
  // Search-on-server is intentionally dropped here — the SB2 endpoint
  // returns the full tenant roster (bounded) and the FE filters
  // client-side. The new endpoint doesn't expose a `q` parameter in
  // 1.0; if the tenant grows past comfortable client-side filter
  // budget (~200 users) we'd add a server-side `q` in a future minor
  // bump.
  const candidatesQuery = useQuery<GetAssigneeCandidatesResponse>({
    queryKey: ["assignee-candidates-sb2", inspectionId],
    enabled: anchorEl !== null,
    queryFn: async () => {
      const response = await api.get<{
        users: readonly {
          userId: string;
          fullName: string;
          email?: string;
          role: string;
        }[];
      }>(
        `/data-acquisitions/${encodeURIComponent(inspectionId)}/assignable-users`,
      );
      // Adapt the AssignableUser shape to the AssigneeCandidate shape so
      // the existing Autocomplete renderer doesn't need changes.
      const items: AssigneeCandidate[] = response.data.users.map((u) => ({
        id: u.userId,
        displayName: u.fullName,
        initials: deriveInitials(u.fullName),
        tenantId: "00000000-0000-0000-0000-000000000000",
        tenantSlug: "",
        tenantDisplayName: null,
        sameTenant: true,
      }));
      return { items };
    },
    staleTime: 30_000,
  });

  const optionsWithUnassign = useMemo<AssigneeCandidate[]>(() => {
    const items = candidatesQuery.data?.items ?? [];
    return [UNASSIGNED_OPTION, ...items];
  }, [candidatesQuery.data?.items]);

  const mutation = useMutation<
    DataAcquisition,
    Error,
    { userId: string | null; displayName: string },
    UpdateContext
  >({
    mutationFn: async ({ userId }) => {
      const body: UpdateAssigneeBody = { userId };
      const response = await api.patch<DataAcquisition>(
        `/data-acquisitions/${encodeURIComponent(inspectionId)}/assignee`,
        body,
      );
      return response.data;
    },
    onMutate: async ({ userId }) => {
      const queryFamily = dataAcquisitionKeys.lists();
      await qc.cancelQueries({ queryKey: queryFamily });
      const cached = qc.getQueriesData<GetDataAcquisitionsResponse>({
        queryKey: queryFamily,
      });
      const ctx: UpdateContext = {
        previousLists: cached
          .filter(
            (
              entry,
            ): entry is [readonly unknown[], GetDataAcquisitionsResponse] =>
              entry[1] !== undefined,
          )
          .map(([key, data]) => ({ key, data })),
      };
      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetDataAcquisitionsResponse>(key, {
          ...data,
          items: data.items.map(
            (row): DataAcquisition =>
              row.id === inspectionId ? { ...row, assignedTo: userId } : row,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        for (const { key, data } of ctx.previousLists) {
          qc.setQueryData(key, data);
        }
      }
      setError("Could not assign — try again");
    },
    onSuccess: (_data, vars) => {
      setSuccess(
        vars.userId === null ? "Unassigned" : `Assigned to ${vars.displayName}`,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: dataAcquisitionKeys.lists() });
    },
  });

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const handlePick = (option: AssigneeCandidate | null) => {
    if (!option) return;
    if (option.id === UNASSIGNED_OPTION.id) {
      mutation.mutate({ userId: null, displayName: "" });
    } else {
      mutation.mutate({
        userId: option.id,
        displayName: option.displayName,
      });
    }
    handleClose();
  };

  const label = current?.displayName ?? fallback ?? "Unassigned";
  const initials = current?.initials ?? "—";

  return (
    <>
      <Chip
        size="small"
        avatar={<Avatar sx={{ fontSize: 11 }}>{initials}</Avatar>}
        label={label}
        deleteIcon={<PencilIcon fontSize="small" aria-label="Edit assignee" />}
        onClick={handleOpen}
        onDelete={handleOpen}
        sx={{ maxWidth: "100%", cursor: "pointer" }}
        aria-haspopup="dialog"
        aria-label={`Assignee: ${label}. Click to change.`}
      />

      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 320, p: 1.5 } } }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ pl: 0.5, display: "block", mb: 0.5 }}
        >
          Assign inspection to
        </Typography>
        <Autocomplete<AssigneeCandidate, false, true>
          openOnFocus
          disableClearable
          size="small"
          options={optionsWithUnassign}
          loading={candidatesQuery.isPending}
          inputValue={search}
          onInputChange={(_e, value) => {
            setSearch(value);
          }}
          getOptionLabel={(o) => o.displayName}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_e, value) => {
            handlePick(value);
          }}
          renderOption={(props, o) => {
            // MUI v6 hands `key` back as part of props so React's
            // diff is stable across re-renders. Pull it out into a
            // local so React's prop-spreading doesn't duplicate it.
            const liProps = props as React.HTMLAttributes<HTMLLIElement> & {
              key?: string;
            };
            const { key: liKey, ...restLi } = liProps;
            return (
              <Box
                component="li"
                key={liKey ?? o.id}
                {...restLi}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Avatar sx={{ width: 24, height: 24, fontSize: 11 }}>
                  {o.initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {o.displayName}
                  </Typography>
                  {o.id !== UNASSIGNED_OPTION.id && !o.sameTenant ? (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {o.tenantDisplayName ?? o.tenantSlug}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          }}
          renderInput={(p) => {
            const { InputLabelProps, InputProps, inputProps, ...rest } = p;
            const tfProps = {
              ...rest,
              placeholder: "Search user…",
              slotProps: {
                htmlInput: inputProps,
                input: {
                  ...InputProps,
                  endAdornment: (
                    <>
                      {candidatesQuery.isPending ? (
                        <CircularProgress size={14} />
                      ) : null}
                      {InputProps.endAdornment}
                    </>
                  ),
                },
                inputLabel: InputLabelProps,
              },
            } as unknown as TextFieldProps;
            return <TextField {...tfProps} />;
          }}
        />
      </Popover>

      <Snackbar
        open={success !== null}
        autoHideDuration={2000}
        onClose={() => {
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => {
            setSuccess(null);
          }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={error !== null}
        autoHideDuration={4000}
        onClose={() => {
          setError(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => {
            setError(null);
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

/** SB2-W-03 — derive 1- or 2-letter initials for the AssignableUser
 *  → AssigneeCandidate adapter. */
function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0];
  if (parts.length === 1) {
    return first ? first.charAt(0).toUpperCase() : "?";
  }
  const last = parts[parts.length - 1];
  return ((first?.charAt(0) ?? "") + (last?.charAt(0) ?? "")).toUpperCase();
}
